import type { FC } from 'react'
import React from 'react'

import type { SkeletonProps } from './types'
import variants from './variants'
import { createUseStyledComponent } from '../../../hooks'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { get } from '../../../utils/object'
import { resolveSize } from '../../../utils/size-utils'

const useStyledComponent = createUseStyledComponent(
  ['span'],
  'csg-skeleton',
  variants
)
const Skeleton: FC<SkeletonProps> = React.forwardRef<
  HTMLDivElement,
  SkeletonProps & { theme?: Theme }
>(function Skeleton(props, ref) {
  const {
    as: component = 'span',
    animation = 'pulse',
    variant = 'text',
    height,
    ...rest
  } = props
  const theme = useSafeThemeUI(props)
  const _height = resolveSize(
    height as string | string[],
    get(theme, 'sizes.font')
  )

  const Component = useStyledComponent(component, variants)

  return (
    <Component
      ref={ref}
      variant={variant}
      __css={{
        display: 'block',
        backgroundColor: 'neutrals.20',
        height: _height ? _height : '1.2em',
        ...(animation === 'pulse'
          ? {
              animation: 'csg-tui-pulse 1.5s ease-in-out 0.5s infinite'
            }
          : animation === 'wave'
            ? {
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  variant: 'gradients.basic',
                  animation: 'csg-tui-wave 1.6s linear 0.5s infinite',
                  content: '""',
                  position: 'absolute',
                  transform: 'translateX(-100%)', // Avoid flash during server-side hydration
                  bottom: 0,
                  left: 0,
                  right: 0,
                  top: 0,
                  zIndex: 1
                }
              }
            : {})
      }}
      {...rest}
    />
  )
})
export default Skeleton
